<template>
  <v-card>
    <v-card-title class="d-flex justify-end align-center flex-sm-nowrap flex-wrap"
      ><!-- Add New Section Button -->
      <v-btn color="primary" variant="text" @click="addSection" prepend-icon="mdi-plus">
        Add Section
      </v-btn>
    </v-card-title>
    <v-card-text>
      <v-expansion-panels v-model="expandedPanel">
        <v-expansion-panel v-for="(section, index) in safeSections" :key="index" :value="index">
          <v-expansion-panel-title>
            <div class="w-100 d-flex justify-space-between align-center">
              <p class="font-weight-bold">
                {{
                  `Section ${index + 1}: ${section?.header?.text ? section.header.text.toUpperCase() : 'New Section'}`
                }}
              </p>
              <v-btn color="error" variant="text" @click="confirmDelete(index)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <HighlightedFeaturesSectionManager
                    :key="index"
                    :section="section"
                    @update:section-settings="updateSection(index, $event)"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>

  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="showDeleteDialog" max-width="400">
    <v-card>
      <v-card-title>Confirm Deletion</v-card-title>
      <v-card-text>Are you sure you want to delete this section?</v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey-darken-1" variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
        <v-btn color="error" variant="text" @click="handleDelete">Delete</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import HighlightedFeaturesSectionManager from './HighlightedFeaturesSectionManager.vue'
import type { IFeaturesSectionSettings } from '@/types'

const pendingDeleteIndex = ref<number | null>(null)
const showDeleteDialog = ref(false)
const expandedPanel = ref(-1)

const props = defineProps<{
  modelValue: IFeaturesSectionSettings[]
}>()

const emit = defineEmits<{
  'update:model-value': [value: IFeaturesSectionSettings[]]
}>()

const ensureSectionStructure = (section: IFeaturesSectionSettings) => {
  if (!section || !section.header || !section.header.text) {
    return getDefaultFeaturesSectionSettings()
  }

  return section
}

const safeSections = computed(() => {
  if (!props.modelValue || !Array.isArray(props.modelValue)) {
    console.error('modelValue is not an array', props.modelValue)
    return []
  }

  return props.modelValue.map((section) => ensureSectionStructure(section))
})

const addSection = () => {
  const newSection: IFeaturesSectionSettings = getDefaultFeaturesSectionSettings()

  emit('update:model-value', [...props.modelValue, newSection])
  expandedPanel.value = props.modelValue.length
}

const updateSection = (index: number, value: IFeaturesSectionSettings) => {
  if (!Array.isArray(props.modelValue)) {
    console.error('modelValue is not an array', props.modelValue)
    emit('update:model-value', [value]) // fallback to ensure at least one section is there
    return
  }

  const updatedSections = [...props.modelValue]
  updatedSections[index] = value
  emit('update:model-value', updatedSections)
}

const confirmDelete = (index: number) => {
  pendingDeleteIndex.value = index
  showDeleteDialog.value = true
}

const handleDelete = () => {
  if (pendingDeleteIndex.value !== null) {
    const updatedSections = [...props.modelValue]
    updatedSections.splice(pendingDeleteIndex.value, 1)
    emit('update:model-value', updatedSections)
  }
  showDeleteDialog.value = false
  pendingDeleteIndex.value = null
}

const getDefaultFeaturesSectionSettings = (): IFeaturesSectionSettings => {
  return {
    header: {
      text: 'New Section',
      colors: {
        color: '#ffffff',
        colorHover: '#ffffff',
        background: '#000000',
        backgroundHover: '#000000',
      },
      typography: {
        fontFamily: 'Arial, sans-serif',
        fontSize: '32',
        fontSizeUnit: 'px',
        fontStyle: 'normal',
        fontWeight: 'normal',
        letterSpacing: '0',
        letterSpacingUnit: 'px',
        lineHeight: '1.5',
        lineHeightUnit: 'em',
        textDecoration: 'none',
        textTransform: 'none',
        textAlignment: 'center',
      },
    },
    features: [],
    generalSettings: {
      backgroundColors: {
        color: '#000000',
        colorHover: '#000000',
        background: '#9E9E9E',
        backgroundHover: '#9E9E9E',
      },
    },
  }
}

onMounted(() => {
  if (!props.modelValue || !Array.isArray(props.modelValue)) {
    console.warn('modelValue is not properly initialized:', props.modelValue)
    emit('update:model-value', [])
  }
})
</script>

<style scoped>
@media (max-width: 425px) {
  :deep(.v-expansion-panel-text__wrapper) {
    padding-left: 10px;
    padding-right: 10px;
  }

  :deep(.v-expansion-panel-text__wrapper > .v-container) {
    padding: 0;
  }

  :deep(.v-card-text) {
    padding-left: 5px;
    padding-right: 5px;
  }
}
</style>
